import type {
  FeatureBlockContent,
  FaqContent,
  HeroContent,
  IconName,
  LinkItem,
  PayrollSavingsCalculatorContent,
  TextSectionContent,
} from './types';

const businessAppBaseUrl = (
  import.meta.env.VITE_BUSINESS_APP_URL ??
  (import.meta.env.DEV ? 'http://localhost:3003' : 'https://nsave.business')
).replace(/\/$/u, '');

export const businessApplyHref = `${businessAppBaseUrl}/registration`;
export const businessCallHref =
  'mailto:business@nsave.com?subject=Request%20an%20nroll%20payroll%20demo';
export const businessWaitlistHref = '/business/waitlist';

export const businessHero: HeroContent = {
  title: 'nroll by nsave: international payroll with $0 transfer fees',
  cta: { label: 'Apply for Payroll', href: businessApplyHref, external: true },
  secondaryCta: { label: 'Request a demo', href: businessCallHref, external: true },
  ctaDetail: '$200 application fee. Refund eligibility and terms apply.',
  ctaNote: {
    text: 'Need a general business account?',
    link: { label: 'Join the waitlist.', href: businessWaitlistHref },
  },
  image: {
    src: '/images/business/hero.jpg',
    width: 2099,
    height: 1200,
    alt: 'nsave business dashboard showing a USD balance and transactions on a desktop monitor in a warm office',
  },
};

export const businessTrust: TextSectionContent = {
  title: 'A payroll account built for international teams',
  body: 'Fund one account, pay your international team and let employees receive their salary in their own nsave accounts.',
  link: { label: 'See How It Works', href: '#how-it-works' },
};

export interface BusinessPath {
  icon: IconName;
  title: string;
  subtitle?: string;
  description: string;
  detail?: string;
  cta: LinkItem;
}

export const businessPaths: { title: string; subtitle?: string; items: BusinessPath[] } = {
  title: 'Looking for a general business account instead?',
  items: [
    {
      icon: 'clock',
      title: 'Join the business account waitlist',
      description:
        'Applications for general business accounts are paused while we improve the product. Join the waitlist for updates and early access when applications reopen.',
      cta: { label: 'Join the Waitlist', href: businessWaitlistHref },
    },
  ],
};

export const businessReceive: FeatureBlockContent = {
  title: 'Reduce the cost of international payroll',
  description: 'Fund, pay and receive in three straightforward steps.',
  image: {
    src: '/images/business/receive.jpg',
    alt: 'nsave multi-currency account showing USD, GBP and EUR balances beside a red notebook',
  },
  rows: [
    {
      icon: 'bank',
      title: '1. Fund your payroll account',
      description:
        'Add payroll funds to your nsave business account before your scheduled payroll run.',
    },
    {
      icon: 'users',
      title: '2. Pay your employees',
      description:
        'Your team opens a free nsave account in minutes using their email. Send salaries to each employee from one account.',
    },
    {
      icon: 'wallet',
      title: '3. Your team receives their salary',
      description:
        'Salary transfers settle instantly in your employees’ nsave accounts with $0 nsave transfer fees.',
    },
  ],
};

export const businessSavingsCalculator: PayrollSavingsCalculatorContent = {
  title: 'Calculate your payroll transfer savings',
  description:
    'Enter your team size and what you currently pay per employee transfer to estimate your savings with $0 nsave transfer fees.',
  employeesLabel: 'Employees paid per run',
  feeLabel: 'Your current fee per transfer (USD)',
  currentCostLabel: 'Your current transfer cost',
  nsaveCostLabel: 'nsave transfer cost',
  savingsLabel: 'Estimated savings:',
  perRunLabel: 'per payroll run',
};

export const businessPayTeam: FeatureBlockContent = {
  title: 'Instant Payroll Transfers for Free',
  description:
    'More of every payroll payment goes to your employees, rather than payment providers.',
  image: {
    src: '/images/business/pay-team.jpg',
    alt: 'A focused professional working at a computer, paying their global team',
  },
  rows: [
    {
      icon: 'percent',
      title: '$0 transfer fees',
      description: 'Pay employees with no nsave fee for transfers between nsave accounts.',
    },
    {
      icon: 'coin',
      title: '$0 monthly payroll subscription',
      description: 'Run payroll without a recurring nsave payroll subscription.',
    },
    {
      icon: 'clock',
      title: 'Instant settlement',
      description: 'Employees receive salary payments in their nsave accounts instantly.',
    },
  ],
};

export const businessGlobalTeam: FeatureBlockContent = {
  title: 'One payroll account for your global team',
  image: {
    ...businessHero.image,
    alt: 'nsave business dashboard used to manage payroll for a global team',
  },
  rows: [
    {
      icon: 'globe',
      title: 'Support international teams',
      description: 'Pay employees and contractors across supported markets.',
    },
    {
      icon: 'at-sign',
      title: 'Pay with an email or ntag',
      description: 'Use simple employee details instead of entering long bank details each month.',
    },
    {
      icon: 'wallet',
      title: 'Give employees more flexibility',
      description: 'Employees choose how to hold, spend or withdraw their salary where available.',
    },
    {
      icon: 'chart',
      title: 'Scale with your team',
      description: 'Add employees and keep payroll details together as your team grows.',
    },
  ],
};

export const businessBulkPayments: FeatureBlockContent = {
  title: 'Bulk Payroll Payments',
  image: businessReceive.image,
  rows: [
    {
      icon: 'users',
      title: 'Pay your whole team in fewer steps',
      description:
        'Send salaries to multiple employees using their ntag or email address, reducing repetitive manual payments.',
      detail: 'Less admin · Fewer manual payments · Easier payroll',
    },
  ],
};

export const businessInvoices: FeatureBlockContent = {
  title: 'Why businesses use nsave for payroll',
  image: {
    src: '/images/business/invoices.jpg',
    alt: 'A red notebook on grid paper with a pencil, ready for payroll administration',
  },
  rows: [
    {
      icon: 'clock',
      title: 'Instant salary payments',
      description:
        'Transfers to your employees instantly, helping your employees get paid reliably and on time.',
    },
    {
      icon: 'globe',
      title: 'Built for international teams',
      description:
        'Pay employees and contractors across multiple markets through one centralised account.',
    },
    {
      icon: 'settings',
      title: 'Less payroll administration',
      description:
        'Bulk payments and simple employee details reduce repetitive manual work for finance and operations teams.',
    },
    {
      icon: 'card',
      title: 'More flexibility for employees',
      description:
        'Employees can access virtual card and investment features where available. Availability varies by market.',
    },
  ],
};

export const businessClosing = {
  title: 'nroll your team today',
  body: 'Apply for an nroll payroll account and pay your international team with $0 nsave transfer fees.',
  image: {
    src: '/images/about/nsave_team_060825-20-1-p-1600.jpg',
    alt: 'The nsave team together in the office',
  },
  primaryCta: { label: 'Apply for Payroll', href: businessApplyHref, external: true },
  secondaryCta: { label: 'Request a demo', href: businessCallHref, external: true },
};

export const businessFaq: FaqContent = {
  title: 'Payroll FAQs',
  items: [
    {
      question: "What if my employee isn't on nsave?",
      answer:
        'They can open a free nsave account in minutes using their email. Once their account is ready, you can add them to your payroll payments.',
    },
    {
      question: 'How long does funding take?',
      answer:
        'Funding times depend on the payment rail and currency you use. You will see the expected arrival time before confirming a transfer.',
    },
    {
      question: 'Which countries are supported?',
      answer:
        'Availability depends on the business and employee markets. Apply or speak with our team to confirm support for your payroll countries.',
    },
    {
      question: 'Is the $200 refundable?',
      answer:
        'Refund eligibility and terms are shown during application. The fee is non-refundable if your application is unsuccessful.',
    },
  ],
};
